import type {
  EventMapBounds,
  EventMapDocument,
  EventMapPoint,
  EventMapSectionView,
  EventMapUnitView,
  EventMapViewportTransform
} from '~/shared-kernel/presentation/event-map/event-map.types'

function rotatePoint(point: EventMapPoint, center: EventMapPoint, rotation: number): EventMapPoint {
  if (!rotation) return point
  const radians = rotation * Math.PI / 180
  const cosine = Math.cos(radians)
  const sine = Math.sin(radians)
  const x = point.x - center.x
  const y = point.y - center.y
  return {
    x: center.x + x * cosine - y * sine,
    y: center.y + x * sine + y * cosine
  }
}

function pointsBounds(points: EventMapPoint[]): EventMapBounds | null {
  if (!points.length) return null
  const xs = points.map(point => point.x)
  const ys = points.map(point => point.y)
  const x = Math.min(...xs)
  const y = Math.min(...ys)
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y }
}

function visualBounds(element: EventMapBounds & { rotation: number; points?: EventMapPoint[] }): EventMapBounds {
  const center = { x: element.x + element.width / 2, y: element.y + element.height / 2 }
  const points = element.points?.length
    ? element.points
    : [
        { x: element.x, y: element.y },
        { x: element.x + element.width, y: element.y },
        { x: element.x + element.width, y: element.y + element.height },
        { x: element.x, y: element.y + element.height }
      ]
  return pointsBounds(points.map(point => rotatePoint(point, center, element.rotation))) ?? element
}

function unionBounds(bounds: EventMapBounds[]): EventMapBounds | null {
  if (!bounds.length) return null
  const x = Math.min(...bounds.map(item => item.x))
  const y = Math.min(...bounds.map(item => item.y))
  const right = Math.max(...bounds.map(item => item.x + item.width))
  const bottom = Math.max(...bounds.map(item => item.y + item.height))
  return { x, y, width: right - x, height: bottom - y }
}

function positiveSize(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : fallback
}

function unitVisualBounds(section: EventMapSectionView, unit: EventMapUnitView): EventMapBounds {
  const center = eventMapUnitPosition(section, unit)
  if (unit.kind === 'seat') {
    const size = positiveSize(unit.markerStyle?.size, 20)
    return { x: center.x - size / 2, y: center.y - size / 2, width: size, height: size }
  }

  const width = positiveSize(unit.width, 1)
  const height = positiveSize(unit.height, 1)
  const corners = [
    { x: center.x - width / 2, y: center.y - height / 2 },
    { x: center.x + width / 2, y: center.y - height / 2 },
    { x: center.x + width / 2, y: center.y + height / 2 },
    { x: center.x - width / 2, y: center.y + height / 2 }
  ]
  return pointsBounds(corners.map(point => rotatePoint(point, center, section.rotation)))
    ?? { x: center.x, y: center.y, width: 0, height: 0 }
}

function sectionContentBounds(document: EventMapDocument, section: EventMapSectionView): EventMapBounds[] {
  const units = document.seatLayouts[section.id]?.units ?? []
  return [
    visualBounds(section),
    ...units.map(unit => unitVisualBounds(section, unit))
  ]
}

export function eventMapDocumentBounds(document: EventMapDocument): EventMapBounds | null {
  return unionBounds([
    ...document.sections.flatMap(section => sectionContentBounds(document, section)),
    ...document.globalElements.map(visualBounds)
  ])
}

export function eventMapLocalityBounds(document: EventMapDocument, localityId: string): EventMapBounds | null {
  return unionBounds(
    document.sections
      .filter(section => section.localityId === localityId)
      .flatMap(section => sectionContentBounds(document, section))
  )
}

export function eventMapUnitPosition(section: EventMapSectionView, unit: EventMapPoint): EventMapPoint {
  const center = { x: section.width / 2, y: section.height / 2 }
  const rotated = rotatePoint(unit, center, section.rotation)
  return { x: section.x + rotated.x, y: section.y + rotated.y }
}

export function eventMapUnitHitArea(unit: EventMapUnitView, zoom: number, minimumScreenSize = 44): EventMapBounds {
  const safeZoom = Math.max(positiveSize(Math.abs(zoom), 0.01), 0.01)
  const minimumWorldSize = positiveSize(minimumScreenSize, 44) / safeZoom
  const seatSize = positiveSize(unit.kind === 'seat' ? unit.markerStyle?.size : undefined, 20)
  const nativeWidth = unit.kind === 'group' ? unit.width : seatSize
  const nativeHeight = unit.kind === 'group' ? unit.height : seatSize
  const width = Math.max(nativeWidth, minimumWorldSize)
  const height = Math.max(nativeHeight, minimumWorldSize)
  return { x: unit.x - width / 2, y: unit.y - height / 2, width, height }
}

export function eventMapFitTransform(
  bounds: EventMapBounds,
  viewport: { width: number; height: number },
  options: { padding?: number; minZoom?: number; maxZoom?: number } = {}
): EventMapViewportTransform {
  const padding = Math.max(0, options.padding ?? 32)
  const minZoom = options.minZoom ?? 0.25
  const maxZoom = options.maxZoom ?? 4
  const availableWidth = Math.max(1, viewport.width - padding * 2)
  const availableHeight = Math.max(1, viewport.height - padding * 2)
  const width = Math.max(1, bounds.width)
  const height = Math.max(1, bounds.height)
  const zoom = Math.min(maxZoom, Math.max(minZoom, Math.min(availableWidth / width, availableHeight / height)))
  return {
    zoom,
    panX: viewport.width / 2 - (bounds.x + bounds.width / 2) * zoom,
    panY: viewport.height / 2 - (bounds.y + bounds.height / 2) * zoom
  }
}
